// Your play history and loves in your account (migration 0018): the half of
// the account sync that follows what you play and what you love, run by the
// engine in accountSync.ts after saved tunes and settings.
//
// * PLAY HISTORY is one row per device. This device writes only its own row
//   (its whole history) and reads the others' to add them up into
//   `otherHistory`: no two devices ever write one counter, so no play is
//   lost when they write at once. The others' rows are fetched only when one
//   of them changed (their update times are compared first), and this
//   device's row is written only when its history changed, under a device id
//   that has settled (data/deviceId.ts).
// * LOVES are one row per account, merged with this device's tune by tune,
//   the newer change winning (`mergeTaste`), and written back against the
//   revision read: when another device saved first, the write is refused, and
//   this one reads again, merges again and saves.
//
// Before either, the device is claimed: another account's recents, history and
// loves are cleared from it (they are in that account), and when you come
// back to a device the history in your own row returns to it before this
// device writes the row again.

import { useStore } from '@/state/store';
import { recordHash, type PersonalSyncMemory } from '@/state/accountSyncPlan';
import {
  combineHistories,
  normalizeTuneHistory,
  pruneHistory,
  recentsAcrossDevices,
  restoreHistory,
  type TuneHistory,
} from '@/state/tuneHistory';
import { mergeTaste, normalizeTasteDoc, type TasteDoc } from '@/state/tuneTaste';
import type { AccountSyncClient, SyncFilter } from './accountSync';
import { DEVICE_SETTLE_MS, thisDevice } from './deviceId';

const HISTORY = 'user_tune_history';
const TASTE = 'user_tune_taste';
/** Device ids per `in()` filter: they travel in the request URL. */
const CHUNK = 40;
/** Times a save is tried again when another device saved first. */
const SAVE_ATTEMPTS = 3;

type Row = Record<string, unknown>;

async function rows(filter: SyncFilter): Promise<Row[]> {
  const { data, error } = await filter;
  if (error) throw new Error(error.message);
  return data ?? [];
}

const EMPTY: TasteDoc = { marks: [], removed: [] };

/** Your marks as they would travel: merged with nothing, so the order and the
 * removals past keeping are the same however they were reached. */
function tasteOf(doc: TasteDoc, now: number): TasteDoc {
  return mergeTaste(doc, EMPTY, now);
}

function sameStamps(a: Record<string, string>, b: Record<string, string> | undefined): boolean {
  if (!b) return false;
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key]);
}

export interface PersonalSyncOptions {
  memory: PersonalSyncMemory;
  now: number;
  /** Run a store write as the sync's own, not a change you made. */
  apply: (write: () => void) => void;
}

export interface PersonalSyncResult {
  memory: PersonalSyncMemory;
  /** Ask again this much later: the history waited for the device id to settle. */
  retryAfter?: number;
}

export async function syncPersonal(
  c: AccountSyncClient,
  uid: string,
  options: PersonalSyncOptions,
): Promise<PersonalSyncResult> {
  const { apply, now } = options;
  apply(() => useStore.getState().claimHistoryFor(uid));
  const memory: PersonalSyncMemory = { ...options.memory };
  const history = await syncHistory(c, uid, options, memory);
  await syncTaste(c, uid, now, apply, memory);
  return { memory, ...(history.retryAfter ? { retryAfter: history.retryAfter } : {}) };
}

// --- play history -------------------------------------------------------------------

async function syncHistory(
  c: AccountSyncClient,
  uid: string,
  { now, apply }: PersonalSyncOptions,
  memory: PersonalSyncMemory,
): Promise<{ retryAfter?: number }> {
  const device = thisDevice(now);
  if (!device) return {};
  const stamps = await rows(c.from(HISTORY).select('device_id, updated_at').eq('owner', uid));
  const own = stamps.find((row) => row.device_id === device.id);
  const others: Record<string, string> = {};
  for (const row of stamps) {
    if (row.device_id === device.id || typeof row.device_id !== 'string') continue;
    others[row.device_id] = typeof row.updated_at === 'string' ? row.updated_at : '';
  }

  // This device has not written its row since this memory began (you are
  // back after another account used it, or the last sync stopped part-way):
  // what your row holds comes back first, by the larger of each count, so
  // the write below can never lose it, and doing it twice changes nothing.
  if (own && memory.history === undefined) {
    const [row] = await rows(
      c.from(HISTORY).select('doc').eq('owner', uid).in('device_id', [device.id]),
    );
    const mine = normalizeTuneHistory(row?.doc);
    const { tuneHistory: local, recentTunes } = useStore.getState();
    const restored = restoreHistory(local, mine);
    if (recordHash(restored) !== recordHash(local)) {
      // The recent tunes come back with it, in the order you opened them.
      const recents = recentsAcrossDevices(recentTunes, restored, restored);
      apply(() =>
        useStore.getState().applyPersonalPull({ tuneHistory: restored, recentTunes: recents }),
      );
    }
  }

  // Your other devices: read again only when one of them wrote.
  if (!sameStamps(others, memory.historyDevices)) {
    const ids = Object.keys(others);
    const docs: TuneHistory[] = [];
    for (let i = 0; i < ids.length; i += CHUNK) {
      for (const row of await rows(
        c
          .from(HISTORY)
          .select('device_id, doc')
          .eq('owner', uid)
          .in('device_id', ids.slice(i, i + CHUNK)),
      )) {
        docs.push(normalizeTuneHistory(row.doc));
      }
    }
    const otherHistory = pruneHistory(combineHistories(...docs));
    if (recordHash(otherHistory) !== recordHash(useStore.getState().otherHistory))
      apply(() => useStore.getState().applyPersonalPull({ otherHistory }));
    memory.historyDevices = others;
  }

  // This device's own row, when its history changed. An empty history writes
  // nothing: there is nothing to add, and a row is never emptied.
  const local = useStore.getState().tuneHistory;
  const hash = recordHash(local);
  if (Object.keys(local).length === 0 || (own && hash === memory.history)) return {};
  if (!device.settled) return { retryAfter: DEVICE_SETTLE_MS };
  const { error } = await c.from(HISTORY).upsert(
    [
      {
        device_id: device.id,
        doc: local as unknown as Row,
        updated_at: new Date(now).toISOString(),
      },
    ],
    {
      onConflict: 'owner,device_id',
    },
  );
  if (error) throw new Error(error.message);
  memory.history = hash;
  return {};
}

// --- loves ---------------------------------------------------------------------------

/** The account's copy and its revision; 0 when there is none yet. */
async function readTaste(
  c: AccountSyncClient,
  uid: string,
): Promise<{ doc: TasteDoc; revision: number }> {
  const [row] = await rows(c.from(TASTE).select('doc, revision').eq('owner', uid));
  return {
    doc: normalizeTasteDoc(row?.doc),
    revision: typeof row?.revision === 'number' ? row.revision : 0,
  };
}

async function saveTaste(
  c: AccountSyncClient,
  doc: TasteDoc,
  expected: number,
): Promise<{ status: 'saved' | 'conflict'; revision: number }> {
  const { data, error } = await c.rpc('save_tune_taste', {
    new_doc: doc as unknown as Row,
    expected_revision: expected,
  });
  if (error) throw new Error(error.message);
  const answer = (data ?? {}) as { status?: unknown; revision?: unknown };
  if (answer.status !== 'saved' && answer.status !== 'conflict')
    throw new Error('Your loves could not be saved.');
  return {
    status: answer.status,
    revision: typeof answer.revision === 'number' ? answer.revision : 0,
  };
}

async function syncTaste(
  c: AccountSyncClient,
  uid: string,
  now: number,
  apply: PersonalSyncOptions['apply'],
  memory: PersonalSyncMemory,
): Promise<void> {
  const [head] = await rows(c.from(TASTE).select('revision').eq('owner', uid));
  const remoteRevision = typeof head?.revision === 'number' ? head.revision : 0;
  const local = () => {
    const s = useStore.getState();
    return tasteOf({ marks: s.tuneTaste, removed: s.tuneTasteRemoved }, now);
  };
  const localHash = recordHash(local());
  // Neither side moved since we last agreed.
  if (remoteRevision === memory.tasteRevision && localHash === memory.taste) return;

  let merged: TasteDoc | null = null;
  let revision = 0;
  for (let attempt = 0; attempt < SAVE_ATTEMPTS && !merged; attempt++) {
    const remote = await readTaste(c, uid);
    const next = mergeTaste(local(), remote.doc, now);
    const nextHash = recordHash(next);
    // A device with nothing marked and no row yet has nothing to say.
    if (remote.revision === 0 && next.marks.length === 0 && next.removed.length === 0) {
      merged = next;
      break;
    }
    if (nextHash === recordHash(tasteOf(remote.doc, now))) {
      merged = next;
      revision = remote.revision;
      break;
    }
    const saved = await saveTaste(c, next, remote.revision);
    if (saved.status === 'saved') {
      merged = next;
      revision = saved.revision;
    }
  }
  if (!merged) throw new Error('Your loves changed on another device while saving. Trying again.');

  // You may have tapped a heart while the requests were out: merge again, so
  // that change stays (and goes up next time).
  const current = local();
  const final = mergeTaste(current, merged, now);
  if (recordHash(final) !== recordHash(current))
    apply(() =>
      useStore.getState().applyPersonalPull({
        tuneTaste: final.marks,
        tuneTasteRemoved: final.removed,
      }),
    );
  memory.taste = recordHash(merged);
  memory.tasteRevision = revision;
}
